/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { backendPropertiesCrud } from '@services/housien';
import { formatPropertyLabel } from '@/shared/utils/property-label';

/** Property ids resolved to display labels; missing ids are simply absent. */
export type PropertyLabels = Record<string, string>;

/**
 * Resolves the properties a set of leases points at into readable labels.
 *
 * Fetched one id at a time rather than through the properties search: the
 * search endpoint pages, so any limit picked here would be a silent cap
 * that shows raw ids again past the boundary. React Query caches per id,
 * so repeated properties across leases cost one request, and the cache
 * survives paging through the list.
 *
 * @param agencyId - Agency owning the properties.
 * @param propertyIds - Ids to resolve; duplicates and blanks are ignored.
 * @returns A map of id to label, the ids whose lookup is still running,
 *   and whether any is.
 */
export function useLeasePropertyLabels(agencyId: string, propertyIds: string[]) {
  const uniqueIds = useMemo(
    () => [...new Set(propertyIds.filter(Boolean))].sort(),
    [propertyIds],
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['leases', agencyId, 'property-label', id] as const,
      queryFn: () => backendPropertiesCrud(agencyId).getById({ id }),
      enabled: Boolean(agencyId && id),
      // Addresses do not move; refetching them while the broker scrolls
      // the list would spend requests on an answer that cannot change.
      staleTime: 5 * 60 * 1000,
    })),
  });

  const labels = useMemo<PropertyLabels>(() => {
    const map: PropertyLabels = {};
    results.forEach((result, index) => {
      if (result.data) map[uniqueIds[index]] = formatPropertyLabel(result.data);
    });
    return map;
  }, [results, uniqueIds]);

  // Per id, so a row can show a placeholder until its own label lands
  // instead of the raw id, which read as a broken name for seconds on a
  // slow network.
  const pending = useMemo(
    () => new Set(uniqueIds.filter((_, index) => results[index]?.isLoading)),
    [results, uniqueIds],
  );

  return { labels, pending, isLoading: pending.size > 0 };
}
